import { useEffect, useRef, useState } from 'react';
import { ProjectLogo } from '@/pages/Judges/components/ProjectLogo';
import type { LiveStageSnapshot } from '@/types/live';

// m:ss, with a leading minus once the clock runs past zero.
function formatSeconds(total: number, overtime = false): string {
  const s = Math.max(0, Math.floor(total));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${overtime ? '-' : ''}${m}:${r.toString().padStart(2, '0')}`;
}

interface ResolvedTimer {
  state: 'running' | 'stopped' | 'idle';
  endsAtMs: number | null;
  remainingSeconds: number | null;
  clockOffsetMs: number;
}

export function TimerView({
  eventSlug,
  stageSlug,
}: {
  eventSlug: string;
  stageSlug: string;
}) {
  const [data, setData] = useState<LiveStageSnapshot | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const offsetRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/${eventSlug}/stages/${stageSlug}/live`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const body = (await res.json()) as LiveStageSnapshot;
        if (cancelled) return;
        offsetRef.current =
          new Date(body.timer.serverNow).getTime() - Date.now();
        setData(body);
      } catch {}
    };
    fetchData();
    const id = setInterval(fetchData, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [eventSlug, stageSlug]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);

  const timer: ResolvedTimer | null = data
    ? {
        state: data.timer.state,
        endsAtMs: data.timer.endsAt
          ? new Date(data.timer.endsAt).getTime()
          : null,
        remainingSeconds: data.timer.remainingSeconds,
        clockOffsetMs: offsetRef.current,
      }
    : null;

  // A running clock keeps counting past zero, into overtime: the seconds since it ran
  // out, shown with a minus.
  const { displaySeconds, isExpired } = (() => {
    if (!timer) return { displaySeconds: 0, isExpired: false };
    if (timer.state === 'running' && timer.endsAtMs != null) {
      const leftMs = timer.endsAtMs - (now + timer.clockOffsetMs);
      return leftMs > 0
        ? { displaySeconds: Math.ceil(leftMs / 1000), isExpired: false }
        : { displaySeconds: Math.floor(-leftMs / 1000), isExpired: true };
    }
    if (timer.state === 'stopped' && timer.remainingSeconds != null) {
      return { displaySeconds: timer.remainingSeconds, isExpired: false };
    }
    return { displaySeconds: 0, isExpired: false };
  })();

  const presenting = data?.presenting ?? null;

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 overflow-hidden bg-background px-6 py-10 text-foreground">
      <style>{`
        @keyframes timer-overtime-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0.15; }
        }
      `}</style>
      {/* Which room this screen is, when stages run in parallel. */}
      {data && (
        <p
          className="fixed top-4 left-6 font-mono text-muted-foreground uppercase tracking-widest"
          style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}
        >
          {data.stage.name}
        </p>
      )}
      <div
        className={`text-center font-bold font-mono tabular-nums leading-none ${
          isExpired ? 'text-destructive' : ''
        }`}
        style={{
          fontSize: 'clamp(7rem, 30vw, 40rem)',
          animation: isExpired
            ? 'timer-overtime-blink 1s step-end infinite'
            : undefined,
        }}
      >
        {formatSeconds(displaySeconds, isExpired)}
      </div>

      {/* Always rendered, so the clock does not jump when the time runs out. */}
      <p
        className="font-bold text-destructive uppercase tracking-widest"
        style={{
          fontSize: 'clamp(1.25rem, 3.5vw, 3rem)',
          visibility: isExpired ? 'visible' : 'hidden',
        }}
        aria-live="assertive"
      >
        Se acabó el tiempo
      </p>

      <div className="flex flex-col items-center gap-2 text-center">
        {presenting && (
          <>
            <h1
              className="flex items-center justify-center gap-[0.4em] font-semibold leading-tight"
              style={{ fontSize: 'clamp(1.5rem, 4vw, 3.5rem)' }}
            >
              {/* Sized in em, so it scales with the name beside it. */}
              <ProjectLogo
                project={presenting.project}
                className="h-[1.6em] w-[1.6em]"
              />
              <span className="min-w-0 break-words">{presenting.project.name}</span>
            </h1>
            <p
              className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-muted-foreground"
              style={{ fontSize: 'clamp(1rem, 2.2vw, 2rem)' }}
            >
              <span className="font-mono">@{presenting.team.slug}</span>
              {presenting.track && (
                <span className="border border-current px-3 py-0.5">
                  {presenting.track.name}
                </span>
              )}
            </p>
          </>
        )}
        {!data && <p className="text-muted-foreground text-sm">Cargando…</p>}
        {data && !presenting && (
          <p className="text-muted-foreground">
            Ningún equipo presentando ahora
          </p>
        )}
      </div>
    </main>
  );
}
